import { Component, Input, OnChanges, ViewChild } from "@angular/core";
import { VTableComponent } from "@app/directives/_layout/v-table/_components/v-table.component";
import { IVTableCell } from "@app/directives/_layout/v-table/_models/IVTableCell";
import { IVTableColumn } from "@app/directives/_layout/v-table/_models/IVTableColumn";
import { IVTableRow } from "@app/directives/_layout/v-table/_models/IVTableRow";
import { VTableService } from "@app/directives/_layout/v-table/_services/v-table.service";
import { SortType } from "@app/enum/SortType";
import { DateService, DateUnit } from "@services/date.service";
import { TranslationService } from "@services/translation.service";
import { UnitConversionService, UnitConversionType } from "@services/unitConversion.service";
import { ITemperatureOutOfBoundsHistory } from "@temperature/_models/temperature/ITemperatureOutOfBoundsHistory";
import { ITemperatureProbe, ITemperatureProbeWithTemperatureData } from "@temperature/_models/temperature/ITemperatureProbe";
import { IVehicleWithTemperatureData } from "@temperature/_models/temperature/IVehicleWithTemperatureData";
import { TemperatureOutOfBoundsHistoryService } from "@temperature/_services/temperature-out-of-bounds.service";
import { TemperatureProbeService } from "@temperature/_services/temperature-probe.service";
import { TemperatureRecordService } from "@temperature/_services/temperature-record.service";
import { lastValueFrom } from "rxjs";
import { ITemperatureRecord } from "../../../_models/temperature/ITemperatureRecord";
import { IStackedProbeReadingData, StackedProbeReadingComponent } from "../stacked-probe-reading/stacked-probe-reading.component";
import './temperature-log.component.scss';
import { SortDirection } from "@app/enum/SortDirection";

interface ITemperatureLogTableRecord {
    Id: number;
    timestamp: Date;
    plot: number;
    probe1: IStackedProbeReadingData;
    probe2?: IStackedProbeReadingData;
    probe3?: IStackedProbeReadingData;
    probe4?: IStackedProbeReadingData;
}

@Component({
    selector: 'temperature-log',
    template: require('./temperature-log.component.html')
})
export class TemperatureLogComponent implements OnChanges {
    @Input() vehicle: IVehicleWithTemperatureData;
    @Input() start: Date;
    @Input() end: Date;

    loading: boolean;
    temperatureLog: ITemperatureLogTableRecord[] = [];
    temperatureOutOfBoundsHistory: ITemperatureOutOfBoundsHistory[];
    temperatureRecordsByDate: any;

    tableColumns: IVTableColumn<ITemperatureLogTableRecord>[];
    tableRows: IVTableRow[];
    exportedFileName: string = this.translationService.t("TempCompliance.Temperatures");

    numberOfProbes = 0;

    UnitConversionType = UnitConversionType;

    @ViewChild('tempLogTable') table: VTableComponent;

    constructor(
        private readonly dateService: DateService,
        private readonly temperatureOutOfBoundsHistoryService: TemperatureOutOfBoundsHistoryService,
        private readonly temperatureProbeService: TemperatureProbeService,
        private readonly temperatureRecordService: TemperatureRecordService,
        private readonly translationService: TranslationService,
        private readonly unitConversionService: UnitConversionService,
        private readonly vTableService: VTableService) { }

    ngOnInit(): void {
    }

    ngOnChanges(): void {
        this.getData();
    }

    //--

    private getData(): void {
        this.loading = true;
        const promises = [];
        promises.push(this.getProbes());
        promises.push(lastValueFrom(this.temperatureRecordService.getLatestTemperatureRecordsForVehicle(this.vehicle.Id)));
        promises.push(lastValueFrom(this.temperatureRecordService.getTemperatureRecordsForVehicle(this.vehicle.Id, this.start, this.end)));
        promises.push(lastValueFrom(this.temperatureOutOfBoundsHistoryService.getVehicleTemperatureOutOfBoundsHistories(this.vehicle.Id, this.dateService.addOrSubtractTime(this.start, -31, DateUnit.Day), this.end)));

        Promise.all(promises).then(([probes, latestRecords, records, outOfBoundsHistories]) => {

            this.temperatureRecordsByDate = this.groupRecordsByDate(records);
            this.temperatureOutOfBoundsHistory = outOfBoundsHistories;

            this.temperatureProbeService.mapTemperatureRecordsToProbes(probes, records);
            probes.forEach(probe => {
                this.temperatureProbeService.mapProbeLatestRecord(probe, latestRecords);
            });

            this.vehicle.temperatureProbes = probes;

        }).finally(() => {
            this.setExportedFileName();
            this.setupTemperatureLog();

            this.tableRows = this.vTableService.createTableRows(this.temperatureLog, this.tableColumns);
            this.loading = false;
        })
    }

    private setupTemperatureLog(): void {
        this.temperatureLog = [];

        if (!this.vehicle?.temperatureProbes) {
            return;
        }

        const probeIds = this.vehicle.temperatureProbes.map((probe, index) => {
            if (index < 4) {
                return probe.Id;
            }
        })

        Object.keys(this.temperatureRecordsByDate).forEach((key, rowId) => {
            const row: ITemperatureLogTableRecord = {
                Id: rowId,
                timestamp: new Date(key),
                plot: null,
                probe1: null,
                probe2: null,
                probe3: null,
                probe4: null
            }

            let rowHasData = false;

            this.temperatureRecordsByDate[key].forEach((record: ITemperatureRecord, index: number) => {
                if (!probeIds.includes(record.TemperatureProbeId)) {
                    return;
                }

                rowHasData = true;

                const probe = this.vehicle.temperatureProbes.find(probe => probe.Id === record.TemperatureProbeId);
                const probeKey = "probe" + (index + 1);
                row[probeKey] = {
                    probeName: probe.cleanName,
                    temperature: this.unitConversionService.convertTemperatureWithSuffix(record.Temperature),
                    outOfBounds: this.probeHasOutOfBoundsHistory(probe, new Date(key))
                };
                row.plot = row.plot || record.PlotId;
            })

            if (!rowHasData) {
                return;
            }

            this.temperatureLog.push(row);
        })

        this.createTable();
    }

    private groupRecordsByDate(records: ITemperatureRecord[]) {
        const groupedRecords = {};
        records.forEach(record => {
            const timestampStr = String(record.Timestamp);
            if (!groupedRecords[timestampStr]) {
                groupedRecords[timestampStr] = [];
            }
            groupedRecords[timestampStr].push(record);
        })
        return groupedRecords;
    }


    private probeHasOutOfBoundsHistory(probe: ITemperatureProbeWithTemperatureData, date: Date): boolean {
        return this.temperatureOutOfBoundsHistory.some(history => {
            return history.TemperatureProbeId === probe.Id &&
                this.dateService.date1IsAfterDate2(date, history.StartUtc, true) &&
                (this.dateService.date1IsAfterDate2(history.EndUtc, date) || !history.EndUtc);
        })
    }

    private getProbes(): Promise<ITemperatureProbe[]> {
        if (this.vehicle.temperatureProbes.length) {
            return Promise.resolve(this.vehicle.temperatureProbes);
        }

        return lastValueFrom(this.temperatureProbeService.getTemperatureProbesForVehicle(this.vehicle.Id));
    }

    private setExportedFileName(): void {
        if (!this.vehicle) {
            return;
        }

        const vehicleReg = this.vehicle.ShortReg;
        const dateRange = this.start.toLocaleDateString();

        this.exportedFileName = `${vehicleReg}_${dateRange}`;
    }

    private createTable(): void {
        this.tableColumns = [
            {
                header: this.vTableService.createDateTableHeader({ title: this.translationService.t('Common.Time'), sortDirection: SortDirection.Descending }),
                createCellFunction: (log) => this.vTableService.createDateCell(log.timestamp)
            },
            {
                header: this.vTableService.createLocationInformationHeader(),
                createCellFunction: (log) => this.vTableService.createLocationInformationCell({ plotId: log.plot })
            }
        ];

        if (this.vehicle.temperatureProbes.length > 0) {
            const maxColumns = Math.min(this.vehicle.temperatureProbes.length, 4);
            for (let index = 1; index <= maxColumns; index++) {
                this.tableColumns.push({
                    header: this.vTableService.createTableHeader({ title: this.translationService.t('TempCompliance.ProbeX', index), sortType: SortType.Number }),
                    createCellFunction: (log) => this.createProbeReadingCell(log, index)
                })
            }
        }
    }

    private createProbeReadingCell(log: ITemperatureLogTableRecord, index: number): IVTableCell {
        const probeKey = `probe${index}`;
        return this.vTableService.createTableCell(log[probeKey], { sortData: log[probeKey]?.temperature, exportData: log[probeKey]?.temperature, component: StackedProbeReadingComponent, componentInputs: { data: log[probeKey] } })
    }

}
